import { setup, assign, fromPromise } from "xstate-5";
import { AxiosError } from "axios";
import { DTOAiReportsDataScienceResponse } from "vue-app/api/dto";
import { xlog } from "vue-app/utils/xstate";
import { InjectionKey } from "vue";
import { useMachine } from "@xstate-5/vue";
import { getAiReport } from "vue-app/api/NewsletterHubService";
import { sendCustomEventToGA } from "vue-app/plugins/edroneGtag";

type ContextMachineReadyFlowsInsight = {
  data: { summary: string | null; introduction: string | null; insights: (string | null)[] } | null;
  activeInsightIndex: number;
  hasOpenDrawerEventBeenSentToGA: boolean;
};

const LOCAL_STORAGE_KEY_FIRST_VISIT = "READY_FLOWS_INSIGHT_DRAWER_VIEWED";
const URL_SEARCH_PARAM_KEY_OPEN_INSIGHT_DRAWER = "open_insight_drawer";

export const machineReadyFlowsInsight = setup({
  types: {
    tags: {} as "drawerOpen",
    context: {} as ContextMachineReadyFlowsInsight,
    events: {} as
      | { type: "OPEN_DRAWER" }
      | { type: "CLOSE_DRAWER" }
      | { type: "SET_ACTIVE_INSIGHT_INDEX"; index: number }
      | { type: "xstate.done.actor.fetchInsights"; output: DTOAiReportsDataScienceResponse }
      | { type: "xstate.error.actor.fetchInsights"; error: AxiosError },
  },
  guards: {
    shouldOpenDrawerAutomaticallyFromURL: () =>
      new URLSearchParams(window.location.search).has(URL_SEARCH_PARAM_KEY_OPEN_INSIGHT_DRAWER),
    shouldOpenDrawerImmediatelyOnFirstVisit: () => !localStorage.getItem(LOCAL_STORAGE_KEY_FIRST_VISIT),
    canSendOpenDrawerEventToGA: ({ context }) => !context.hasOpenDrawerEventBeenSentToGA,
  },
  actions: {
    assignInsights: assign(({ event, context }, params: Required<ContextMachineReadyFlowsInsight["data"]>) => {
      xlog({ label: "assignInsights", type: "action", event, context });
      return {
        ...context,
        data: params,
        activeInsightIndex: 0,
      };
    }),
    setActiveInsightIndex: assign(({ event, context }, params: number) => {
      xlog({ label: "setActiveInsightIndex", type: "action", event, context });
      sendCustomEventToGA("smart-insights-drawer-item-change", { insight: params + 1 });

      return {
        ...context,
        activeInsightIndex: params,
      };
    }),
    sendOpenDrawerEventToGA: assign(({ event, context }) => {
      xlog({ label: "sendOpenDrawerEventToGA", type: "action", event, context });

      const isDrawerOpenByURL = new URLSearchParams(window.location.search).has(
        URL_SEARCH_PARAM_KEY_OPEN_INSIGHT_DRAWER
      );

      if (isDrawerOpenByURL) {
        sendCustomEventToGA("smart-insights-drawer-open-via-url");
      } else {
        sendCustomEventToGA("smart-insights-drawer-open");
      }

      return {
        ...context,
        activeInsightIndex: 0,
        hasOpenDrawerEventBeenSentToGA: true,
      };
    }),
    closeDrawerRemoveUrlParamsAndSetAsVisited: assign(({ context, event }) => {
      xlog({ label: "closeDrawerRemoveUrlParamsAndSetAsVisited", type: "action", context, event });

      const url = new URL(window.location.href);
      url.searchParams.delete(URL_SEARCH_PARAM_KEY_OPEN_INSIGHT_DRAWER);
      window.history.replaceState({}, document.title, url.toString());

      localStorage.setItem(LOCAL_STORAGE_KEY_FIRST_VISIT, "true");

      sendCustomEventToGA("smart-insights-drawer-close");

      return {
        ...context,
        hasOpenDrawerEventBeenSentToGA: false,
      };
    }),
  },
  actors: {
    fetchInsights: fromPromise(async () => {
      xlog({ label: "fetchInsights" });

      const data = await getAiReport({
        language: window.EDRONE_CONTEXT.user.lang,
        trackerId: window.EDRONE_CONTEXT.trackerId,
      });

      return {
        summary: data.summary,
        insights: data.insights,
        introduction: data.introduction,
      };
    }),
  },
}).createMachine({
  id: "machineReadyFlowsInsight",
  initial: "fetchingInsights",
  context: {
    data: null,
    activeInsightIndex: 0,
    hasOpenDrawerEventBeenSentToGA: false,
  },
  states: {
    fetchingInsights: {
      invoke: {
        src: "fetchInsights",
        onError: "error",
        onDone: {
          target: "fetchedInsights",
          actions: [
            {
              type: "assignInsights",
              params: ({ event }) => event.output,
            },
          ],
        },
      },
    },
    fetchedInsights: {
      initial: "drawerClosed",
      always: [
        {
          target: ".drawerOpen",
          guard: "shouldOpenDrawerAutomaticallyFromURL",
        },
        {
          target: ".drawerOpen",
          guard: "shouldOpenDrawerImmediatelyOnFirstVisit",
        },
      ],
      states: {
        drawerClosed: {
          on: {
            OPEN_DRAWER: "drawerOpen",
          },
        },
        drawerOpen: {
          tags: ["drawerOpen"],
          always: {
            guard: "canSendOpenDrawerEventToGA",
            actions: "sendOpenDrawerEventToGA",
          },
          on: {
            CLOSE_DRAWER: {
              target: "drawerClosed",
              actions: "closeDrawerRemoveUrlParamsAndSetAsVisited",
            },
            SET_ACTIVE_INSIGHT_INDEX: {
              target: "drawerOpen",
              actions: [
                {
                  type: "setActiveInsightIndex",
                  params: ({ event }) => event.index,
                },
              ],
            },
          },
        },
      },
    },
    error: {},
  },
});

export const symbolMachineReadyFlowsInsight: InjectionKey<
  ReturnType<typeof useMachine<typeof machineReadyFlowsInsight>>
> = Symbol();
